import { Component, computed, input, output, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxColor, OnyxIconName } from '@onyx/angular';
import { OnyxIconComponent, OnyxTooltipDirective } from '@onyx/angular';
import { ValidationTooltipComponent } from '../../../../../../../../common/components/tooltips/validation-tooltip/validation-tooltip.component';
import type { ValidationIssue } from '../../../../../../../../common/interfaces/validation/validation-issue';
import type { SwapSetError } from '../../../../../../common/enums/swap/swap-error';
import { SwapSetAction } from '../../../../../../common/enums/swap/swap-set-action';
import type { SwapVehicleType } from '../../../../../../common/enums/swap/swap-vehicle-type';
import { SwapDetailsHelper } from '../../../../../../common/helpers/swap/swap-details.helper';

@Component({
  selector: 'app-map-swap-panel-details-section-swap-point-item',
  imports: [
    OnyxIconComponent,
    OnyxTooltipDirective,
    TranslatePipe,
    ValidationTooltipComponent,
  ],
  templateUrl:
    './map-swap-panel-details-section-swap-point-item.component.html',
  styleUrl: './map-swap-panel-details-section-swap-point-item.component.scss',
  host: {
    '[class]': `active() ? color() : ''`,
    '[class.error]': `errors()?.length`,
    '[class.active]': `active()`,
    '[class.inactive]': `inactive()`,
    '[class.modal]': `isModal()`,
    '[style.height]': `height()`,
    '(mouseenter)': `setHovered(true, 'host')`,
    '(mouseleave)': `setHovered(false, 'host')`,
    '(click)': `showModal($event, 'host')`,
  },
})
export class MapSwapPanelDetailsSectionSwapPointItemComponent {
  public readonly action = input<SwapSetAction>(SwapSetAction.NONE);
  public readonly type = input<SwapVehicleType | null>(null);
  public readonly color = input.required<OnyxColor>();
  public readonly iconName = input.required<OnyxIconName>();
  public readonly height = input('40px');
  public readonly errors = input<SwapSetError[]>();
  public readonly issues = input<ValidationIssue[]>([]);
  public readonly placeholder = input(false);
  public readonly inactive = input(false);
  public readonly inactiveHover = input(false);
  public readonly forceActive = input(false);
  public readonly variant = input<'form' | 'modal'>('form');

  public readonly openModal = output<void>();

  protected readonly I18N = 'map.swapPanel.errors';

  protected readonly SwapSetAction = SwapSetAction;

  protected actionLabel = computed(() => {
    const type = this.type();
    const action = this.action();

    if (!type || action === SwapSetAction.NONE) return null;

    return SwapDetailsHelper.getActionLabelKey(action, type, 'past');
  });

  protected isHovered = signal(false);

  protected isModal = computed(() => this.variant() === 'modal');
  protected showSideModalIcon = computed(
    () => !this.placeholder() && this.isHovered(),
  );
  protected iconColor = computed(() =>
    this.errors()?.length ? 'red' : this.color(),
  );
  protected active = computed(
    () => this.forceActive() || this.action() !== SwapSetAction.NONE,
  );

  protected setHovered(isHovered: boolean, target: 'host' | 'icon'): void {
    if (this.isModal() !== (target === 'host')) return;
    if (isHovered && this.inactiveHover()) return;

    this.isHovered.set(isHovered);
  }

  protected showModal(event: Event, target: 'host' | 'icon'): void {
    if (this.isModal() !== (target === 'host')) return;
    if (this.placeholder()) return;
    if (target === 'icon' && !this.showSideModalIcon()) return;

    this.openModal.emit();
    event.stopPropagation();
  }
}
